/* Progressive quick add: native product forms remain usable without JavaScript. */
if (!customElements.get('objectiv-shop-quick-add')) {
  customElements.define('objectiv-shop-quick-add', class extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.button = this.querySelector('[type="submit"]');
      this.label = this.querySelector('[data-cart-label]');
      this.idleLabel = this.label?.innerHTML;
      this.status = this.querySelector('[role="status"]');
      if (!this.form || this.lifetime) return;
      this.lifetime = new AbortController();
      this.form.addEventListener('submit', event => this.add(event), { signal: this.lifetime.signal });
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-objectiv-quantity]');
        if (!button || !this.contains(button)) return;
        const input = this.form.elements.quantity;
        if (!input) return;
        const minimum = Number(input.min) || 1;
        const step = Number(input.step) || 1;
        const maximum = input.max ? Number(input.max) : Infinity;
        const current = Math.max(minimum, Number.parseInt(input.value, 10) || minimum);
        const next = button.dataset.objectivQuantity === 'increase' ? current + step : current - step;
        input.value = Math.min(maximum, Math.max(minimum, next));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }, { signal: this.lifetime.signal });
    }

    disconnectedCallback() {
      this.lifetime?.abort();
      this.lifetime = null;
    }

    async add(event) {
      event.preventDefault();
      if (this.button.disabled || this.busy) return;
      const variantId = this.form.elements.id.value;
      if (!variantId) return;
      this.busy = true;
      this.dataset.cartState = 'adding';
      this.button.disabled = true;
      this.button.setAttribute('aria-busy', 'true');
      this.label.textContent = 'Adding…';
      this.status.textContent = '';
      const data = new FormData(this.form);
      data.delete('return_to');
      const cart = document.querySelector('cart-drawer, cart-notification');
      const sections = cart?.getSectionsToRender?.().map(section => section.id) || ['cart-icon-bubble'];
      data.set('sections', sections.join(','));
      data.set('sections_url', location.pathname);
      cart?.setActiveElement?.(this.button);
      try {
        const response = await fetch(this.form.action.replace(/(?:\.js)?$/, '.js'), {
          method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: data, signal: this.lifetime.signal
        });
        // An OK response whose body cannot be read (a third-party script can
        // consume it) still added the product.
        const result = await response.json().catch(() => (response.ok ? {} : { status: response.status }));
        if (!response.ok || result.status) throw new Error(result.description || 'Unable to add this product. Please try again.');
        // The add succeeded even if optional cart UI refresh fails. Never retry it automatically.
        this.dataset.cartState = 'added';
        let painted = false;
        if (cart?.renderContents && sections.every(id => result.sections?.[id])) {
          try {
            cart.classList.remove('is-empty');
            cart.renderContents(result);
            painted = true;
          } catch (_) { /* Fall back to re-reading the bag below. */ }
        }
        if (!painted && cart?.refresh) {
          // Re-read the bag with plain GETs rather than repeating the add.
          try { Object.assign(result, await cart.refresh({ open: true })); painted = true; }
          catch (_) { /* Optional UI failures must not report the successful add as failed. */ }
        }
        if (painted) {
          // The bag and bubble are current.
        } else if (result.sections?.['cart-icon-bubble']) {
          const fragment = new DOMParser().parseFromString(result.sections['cart-icon-bubble'], 'text/html');
          const bubble = document.getElementById('cart-icon-bubble');
          const content = fragment.querySelector('.shopify-section');
          if (bubble && content) bubble.innerHTML = content.innerHTML;
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          try {
            Promise.resolve(publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'objectiv-shop-quick-add', productVariantId: variantId, cartData: result
            })).catch(() => {});
          } catch (_) { /* Subscriber failures must not report a successful add as failed. */ }
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.dataset.cartState = 'error';
          this.status.textContent = error.message || 'Unable to add this product. Please try again.';
        }
      } finally {
        this.label.innerHTML = this.idleLabel || 'Add To Bag';
        this.busy = false;
        this.button.disabled = false;
        this.button.removeAttribute('aria-busy');
      }
    }
  });
}
